import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import { BrandButton } from "../BrandButton/BrandButton";
import { getAxiosClient } from "../../services/HttpsService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { useParams } from "react-router-dom";
import { fetchRules } from "../../services/state/rules/rules-async-calls";

export const AddRuleModal = ({
  isOpen,
  onClose,
  edit = false,
  tenant,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  tenant?: any;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId } = useParams();

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false); // Track loading state

  const selectedApplication = useSelector(
    (state: RootState) => state.applications.selectedApplication
  );

  useEffect(() => {
    if (edit && tenant) {
      setName(tenant.name);
    }
  }, [edit, tenant]);

  const onCreate = async () => {
    if (name !== "") {
      setLoading(true); // Set loading state to true when request starts
      const url = "/api/eza_app_rule";
      const data = {
        ruleNm: selectedApplication?.appPrefix
          ? selectedApplication.appPrefix + "_" + name
          : name,
        appUuid: applicationId,
      };

      try {
        const response = await getAxiosClient().post(url, data);

        if (response.status === 201) {
          toast({
            title: "Successfully created new process",
            description: "Created new process with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchRules(applicationId as string));
          setName("");
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating tenant:", error);
        toast({
          title: "Error",
          description: "An error occurred while creating new process.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Set loading state back to false after request finishes
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onUpdate = async () => {
    if (name !== "") {
      setLoading(true); // Set loading state to true when request starts
      const url = `/tenant/${tenant?.id}`;
      const data = {
        isActive: true,
      };

      try {
        const response = await getAxiosClient().put(url, data);
        if (response.status === 202) {
          toast({
            title: "Successfully updated process",
            description: "Updated process with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchRules(applicationId as string));
          setName("");
        } else {
          toast({
            title: "Some error has occurred",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error updating process:", error);
        toast({
          title: "Error",
          description: "An error occurred while updating the process.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Set loading state back to false after request finishes
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="20px"
        color="white"
      >
        <ModalCloseButton color="white" />
        <ModalBody>
          {edit ? (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Edit Rule
            </Text>
          ) : (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Create New Rule
            </Text>
          )}
          <FormControl isRequired>
            <FormLabel color="white">Name</FormLabel>
            <InputGroup>
              {selectedApplication?.appPrefix && (
                <InputLeftAddon
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                >
                  {selectedApplication.appPrefix}_
                </InputLeftAddon>
              )}
              <Input
                placeholder="Enter name of the rule"
                focusBorderColor="brand.500"
                ref={initialRef}
                fontSize="18px"
                value={name}
                onChange={(e) => setName(e.target.value)}
                overflowX="auto"
                isRequired
                disabled={edit}
                bg={
                  edit
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(255, 255, 255, 0.1)"
                }
                borderColor="rgba(255, 255, 255, 0.2)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                }}
                _disabled={{
                  bg: "rgba(255, 255, 255, 0.05)",
                  color: "rgba(255, 255, 255, 0.5)",
                }}
              />
            </InputGroup>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <BrandButton
            onClick={edit ? onUpdate : onCreate}
            isLoading={loading} // Show loading indicator on button
            isDisabled={loading}
          >
            {edit ? "Save Changes" : "Create"}
          </BrandButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
